import React from 'react';

import { DRAG_HANDLE_SELECTOR } from './constants';

import './ErrorBoundary.scss';

// Fallback header carries the drag-handle class so a crashed card can still
// be moved on the grid.
const HANDLE_CLASS = DRAG_HANDLE_SELECTOR.slice(1);

interface Props {
  /** Fallback header text ("Performance", "FFA Overview", …). */
  label?: string;
  children: React.ReactNode;
}

interface State {
  error: Error | null;
  generation: number;
}

/** Render-error containment: a crashing child degrades to one inert card
 *  (Retry = remount) instead of unmounting the whole tree. Dependency-free —
 *  safe to deep-import from eager chrome without pulling the grid barrel. */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, generation: 0 };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error(
      `ErrorBoundary[${this.props.label ?? '?'}]`,
      error,
      info.componentStack,
    );
  }

  private retry = (): void => {
    this.setState((s) => ({ error: null, generation: s.generation + 1 }));
  };

  render(): React.ReactNode {
    const { error, generation } = this.state;
    if (error) {
      return (
        <div className="wb-error-boundary" role="alert">
          <div className={`wb-error-boundary__head ${HANDLE_CLASS}`}>
            {this.props.label ?? 'Panel'}
          </div>
          <div className="wb-error-boundary__body">
            <div className="wb-error-boundary__title">Something broke here</div>
            <div className="wb-error-boundary__message">{error.message}</div>
            <button
              type="button"
              className="wb-error-boundary__retry"
              onClick={this.retry}
            >
              Retry
            </button>
          </div>
        </div>
      );
    }
    return <React.Fragment key={generation}>{this.props.children}</React.Fragment>;
  }
}
